import { LOAD_CATALOGS_SUCCESS, LOAD_HOME_DATA_SUCCESS } from './actions';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { fetchBidIncrements } from '@/redux/api/bidIncrements';
import { getDeployment } from './config';
import { getItemCardModel } from './itemCardModel';
import { GlobalState } from '../rootReducer';
import isEmpty from 'lodash/isEmpty';
import { LOAD_SELLER_ENDED_CATALOGS_SUCCESS } from './sellerEndedCatalogs';
import { LOAD_SELLER_UPCOMING_CATALOGS_SUCCESS } from './sellerUpcomingCatalogs';
import ms from 'ms';
import union from 'lodash/union';
import uniqBy from 'lodash/uniqBy';

/* Action Types */
export const LOAD_BID_INCREMENTS_FAIL = 'la/domain/bidIncrements/LOAD_FAIL';
export const LOAD_BID_INCREMENTS_REQUEST = 'la/domain/bidIncrements/LOAD_REQUEST';
export const LOAD_BID_INCREMENTS_SUCCESS = 'la/domain/bidIncrements/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

export type State = {
    byId: { [catalogId: number]: any };
    loaded: { [catalogId: number]: number };
    loading: number[];
};

export const DEFAULT_STATE: State = {
    byId: {},
    loaded: {},
    loading: [],
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}): State {
    let existing: State['byId'];
    let loaded: State['loaded'];
    let loading: State['loading'];
    let time: number;

    switch (action.type) {
        case LOAD_BID_INCREMENTS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.catalogId]),
            };
        case LOAD_BID_INCREMENTS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_BID_INCREMENTS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;
            action.payload.forEach((item) => {
                existing[item.catalogId] = uniqBy(item.bidIncrements, ({ fromAmount }) => fromAmount);
                loaded[item.catalogId] = time;
                loading = difference(loading, [item.catalogId]);
            });
            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        case LOAD_CATALOGS_SUCCESS:
        case LOAD_HOME_DATA_SUCCESS:
        case LOAD_SELLER_UPCOMING_CATALOGS_SUCCESS:
        case LOAD_SELLER_ENDED_CATALOGS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (action.payload.bidIncrements) {
                action.payload.bidIncrements.forEach((item) => {
                    existing[item.catalogId] = uniqBy(item.bidIncrements, ({ fromAmount }) => fromAmount);
                    loaded[item.catalogId] = time;
                    loading = difference(loading, [item.catalogId]);
                });
            }

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.bidIncrements;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getBidIncrements: (state: any, selector: any) => any = createSelector(
    [byIdSelector, idSelector],
    (byId, id) => byId[id] || []
);

export const getBidIncrementsByItemId: (any, number) => any = createSelector(
    [byIdSelector, getItemCardModel],
    (byId, item: any) => byId[item.catalogId] || []
);

const getLoadTimeForBidIncrements = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const isBidIncrementsLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

const shouldFetchBidIncrements = createSelector(
    [idSelector, getBidIncrements, getLoadTimeForBidIncrements, isBidIncrementsLoading],
    (catalogId, item, loaded, loading) => {
        if (!catalogId) {
            return false;
        }
        if (!isEmpty(item)) {
            const time = Date.now();
            const diff = time - loaded;
            if (diff < REDUX_STORE_TIME) {
                return false;
            }
        }
        return !loading;
    }
);

/* ACTION CREATORS */
export const fetchBidIncrementsIfNeeded = (catalogId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    if (shouldFetchBidIncrements(state, catalogId)) {
        const deployment = getDeployment(state);
        try {
            dispatch({
                meta: { actionTime: Date.now() },
                payload: catalogId,
                type: LOAD_BID_INCREMENTS_REQUEST,
            });
            const response = await fetchBidIncrements({ catalogId, deployment });
            dispatch({
                meta: { actionTime: Date.now(), catalogId },
                payload: response.data,
                type: LOAD_BID_INCREMENTS_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                meta: { catalogId },
                payload: error,
                type: LOAD_BID_INCREMENTS_FAIL,
            });
        }
    } else {
        return Promise.resolve();
    }
};
